import { useCallback, useEffect, useMemo, useState } from "react";
import { StatusChip } from "../components/StatusChip.js";
import { api, ApiError } from "../lib/api.js";
import { clearOnEscape } from "../lib/clearOnEscape.js";
import { hashForRoute } from "../lib/hashRoute.js";
import { filterJobs } from "../lib/jobsFilter.js";
import { NON_TERMINAL_JOB_STATUSES } from "../lib/types.js";
import type { Job, JobsResponse, JobStatus } from "../lib/types.js";
import { useDebounced } from "../lib/useDebounced.js";
import { usePolling } from "../lib/usePolling.js";

const inputCls =
  "w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 " +
  "outline-none focus:ring-2 focus:ring-(--accent) dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100";

const selectCls =
  "rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 " +
  "outline-none focus:ring-2 focus:ring-(--accent) dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100";

const STATUSES: JobStatus[] = ["queued", "claimed", "running", "succeeded", "failed", "cancelled"];

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function when(iso?: string): string {
  return iso ? new Date(iso).toLocaleString() : "-";
}

function duration(ms?: number): string {
  if (ms === undefined) return "-";
  return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`;
}

function usd(value?: number): string {
  return value === undefined ? "-" : `$${value.toFixed(4)}`;
}

/** The job-history table (GSI3, one day partition per page). Filters: a status
 *  select, a day picker (defaults to today), and a free-text match over route
 *  id and error code - Escape clears it, an in-field X clears it, and its
 *  effect debounces per the cross-project UI standard. */
export function Jobs({ status: initialStatus, day: initialDay }: { status?: string; day?: string }) {
  const [day, setDay] = useState(initialDay || today());
  const [status, setStatus] = useState(initialStatus ?? "");
  const [text, setText] = useState("");
  const debouncedText = useDebounced(text);

  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // A deep link that changes while this page is already mounted (e.g. the
  // Dashboard's "In flight" then "Last 24 hours" links) still re-seeds the
  // filters.
  useEffect(() => {
    setDay(initialDay || today());
    setStatus(initialStatus ?? "");
  }, [initialStatus, initialDay]);

  const load = useCallback(async () => {
    try {
      const qs = new URLSearchParams({ day });
      if (status) qs.set("status", status);
      const res = await api<JobsResponse>(`/api/jobs?${qs.toString()}`);
      setJobs(res.items);
      setError(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return;
      setError(err instanceof Error ? err.message : "Could not load jobs.");
    } finally {
      setLoading(false);
    }
  }, [day, status]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  const visible = useMemo(
    () => filterJobs(jobs, { status, text: debouncedText }),
    [jobs, status, debouncedText],
  );

  // Keep the table live while anything on it is still working.
  const anyInFlight = useMemo(
    () =>
      visible.some((j) => (NON_TERMINAL_JOB_STATUSES as readonly string[]).includes(j.status)),
    [visible],
  );
  usePolling(load, { enabled: anyInFlight });

  return (
    <div className="mx-auto max-w-5xl p-6">
      <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Jobs</h1>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        Every generation job for one day, newest first.
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <input
          type="date"
          className={selectCls}
          value={day}
          max={today()}
          onChange={(e) => setDay(e.target.value || today())}
          aria-label="Day"
        />
        <select
          className={selectCls}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          aria-label="Status"
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <div className="relative min-w-[12rem] flex-1">
          <input
            className={inputCls}
            placeholder="Filter by route or error code..."
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={clearOnEscape(text, setText)}
          />
          {text && (
            <button
              type="button"
              aria-label="Clear filter"
              onClick={() => setText("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            >
              &times;
            </button>
          )}
        </div>
      </div>

      {error && <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>}

      {loading ? (
        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">Loading...</p>
      ) : visible.length === 0 ? (
        <p className="mt-8 text-center text-sm text-gray-500 dark:text-gray-400">
          No jobs match.
        </p>
      ) : (
        <div className="mt-4 overflow-x-auto rounded-md border border-gray-200 dark:border-gray-800">
          <table className="w-full min-w-[860px] text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500 dark:bg-gray-800 dark:text-gray-400">
              <tr>
                <th className="px-3 py-2">Job</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Route</th>
                <th className="px-3 py-2">Created</th>
                <th className="px-3 py-2">Duration</th>
                <th className="px-3 py-2">USD</th>
                <th className="px-3 py-2">Error</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {visible.map((j) => (
                <tr key={j.jobId} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-3 py-2">
                    <a
                      href={hashForRoute({ kind: "itemDetail", projectId: j.projectId, itemId: j.itemId })}
                      className="font-mono text-xs text-(--accent) hover:underline"
                    >
                      {j.jobId.slice(0, 8)}
                    </a>
                  </td>
                  <td className="px-3 py-2">
                    <StatusChip status={j.status} label={j.status} />
                  </td>
                  <td className="px-3 py-2">{j.contentType}</td>
                  <td className="px-3 py-2">{j.routeId}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-500 dark:text-gray-400">
                    {when(j.createdAt)}
                  </td>
                  <td className="px-3 py-2">{duration(j.durationMs)}</td>
                  <td className="px-3 py-2">{usd(j.usd)}</td>
                  <td className="px-3 py-2 text-red-600 dark:text-red-400">
                    {j.error ? (
                      <>
                        <span className="font-medium">{j.error.code}</span>
                        <span className="block text-xs text-gray-500 dark:text-gray-400">
                          {j.error.message}
                        </span>
                      </>
                    ) : (
                      "-"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
